import { reducer, initialState } from './machine.ts';
import type { SessionState, Action, Dispatch } from './types.ts';

const STORAGE_KEY = 'eco_session';

// State listeners
type Listener = (state: SessionState) => void;
const listeners: Set<Listener> = new Set();

// Current state
let currentState: SessionState = initialState;

// Load state from sessionStorage
export function loadState(): SessionState | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as SessionState;
  } catch (error) {
    console.error('Failed to load state from sessionStorage:', error);
    return null;
  }
}

// Save state to sessionStorage
export function saveState(state: SessionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save state to sessionStorage:', error);
  }
}

// Clear stored state
export function clearState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear state from sessionStorage:', error);
  }
}

// Dispatch action
export const dispatch: Dispatch = (action: Action) => {
  currentState = reducer(currentState, action);
  
  // Save to sessionStorage after every action
  saveState(currentState);
  
  // Notify listeners
  listeners.forEach((listener) => listener(currentState));
};

// Subscribe to state changes
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  // Return unsubscribe function
  return () => {
    listeners.delete(listener);
  };
}

// Get current state
export function getState(): SessionState {
  return currentState;
}

// Initialize store
export function initStore(): void {
  const stored = loadState();
  if (stored) {
    currentState = stored;
    // Notify listeners of initial state
    listeners.forEach((listener) => listener(currentState));
  }
}

// Reset store to initial state
export function resetStore(): void {
  dispatch({ type: 'RESET' });
  clearState();
}
